'use client';

import Image from 'next/image';
import { GraduationCap, BarChart3, Clock, Sparkles, Heart, Calculator, GitCompare, Menu, X, LogIn, LogOut } from 'lucide-react';
import { PremiumBadge } from './PremiumBadge';
import { usePremium } from '@/contexts/PremiumContext';
import { useState } from 'react';
import { useSession, signOut } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { LanguageSelector } from './LanguageSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export function Header() {
  const { isPremium } = usePremium();
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: session, status } = useSession();
  const t = useTranslations('Header');

  const handleSignOut = async () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('justLoggedOut', '1');
    }
    await signOut({ redirect: false });
  };

  const navLinks = [
    { href: '/' as const, label: t('home'), icon: null },
    { href: '/favorites' as const, label: t('favorites'), icon: <Heart className="w-4 h-4" /> },
    { href: '/history' as const, label: t('history'), icon: <Clock className="w-4 h-4" /> },
    { href: '/cosmic-origins' as const, label: t('cosmicOrigins'), icon: <Sparkles className="w-4 h-4" /> },
    ...(isPremium ? [
      { href: '/compare' as const, label: t('compare'), icon: <GitCompare className="w-4 h-4" /> },
      { href: '/calculator' as const, label: t('calculator'), icon: <Calculator className="w-4 h-4" /> },
      { href: '/quiz' as const, label: t('quiz'), icon: <GraduationCap className="w-4 h-4" /> },
      { href: '/revision' as const, label: t('revision'), icon: <BarChart3 className="w-4 h-4" /> },
    ] : []),
    { href: '/about' as const, label: t('about'), icon: null },
  ];

  const linkClass = "text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-700 hover:text-[#64ffda] dark:hover:text-[#64ffda] light:hover:text-teal-600 transition-colors font-medium flex items-center gap-1";

  return (
    <header className="bg-[#112240] dark:bg-[#112240] light:bg-white border-b border-[#233554] dark:border-[#233554] light:border-gray-200 sticky top-0 z-50 backdrop-blur-sm bg-opacity-95">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <Image
                src="/logo/logo.png"
                alt="Elementium Logo"
                width={48}
                height={48}
                className="w-full h-full"
                priority
              />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#64ffda] dark:text-[#64ffda] light:text-teal-600 tracking-tight">
                Elementium
              </h1>
              <p className="text-xs text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-600 hidden sm:block">
                {t('tagline')}
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:block">
            <ul className="flex items-center gap-4">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className={linkClass}>
                    {link.icon}
                    {link.label}
                  </Link>
                </li>
              ))}
              {!isPremium && (
                <li>
                  <Link href="/premium" className="text-[#FFD700] hover:text-[#FFA500] transition-colors font-semibold">
                    {t('premium')}
                  </Link>
                </li>
              )}
              <li><PremiumBadge /></li>
              <li><LanguageSelector /></li>
              <li><AccountSection session={session} status={status} onSignOut={handleSignOut} /></li>
            </ul>
          </nav>

          {/* Mobile right side */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSelector />
            <AccountSection session={session} status={status} onSignOut={handleSignOut} />
            <PremiumBadge />
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t('closeMenu') : t('openMenu')}
              className="text-[#a8b2d1] hover:text-[#64ffda] transition-colors p-1"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <nav className="lg:hidden mt-4 pb-2 border-t border-[#233554] pt-4">
            <ul className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className={linkClass} onClick={() => setMenuOpen(false)}>
                    {link.icon}
                    {link.label}
                  </Link>
                </li>
              ))}
              {!isPremium && (
                <li>
                  <Link
                    href="/premium"
                    className="text-[#FFD700] hover:text-[#FFA500] transition-colors font-semibold"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t('premium')}
                  </Link>
                </li>
              )}
              {status === 'authenticated' && session?.user && (
                <li>
                  <button
                    onClick={() => { handleSignOut(); setMenuOpen(false); }}
                    className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors font-medium"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('signOut')}
                  </button>
                </li>
              )}
              {status !== 'loading' && !session && (
                <li>
                  <Link
                    href="/auth/signin"
                    className="flex items-center gap-2 text-[#64ffda] hover:text-[#52d4c2] transition-colors font-medium"
                    onClick={() => setMenuOpen(false)}
                  >
                    <LogIn className="w-4 h-4" />
                    {t('signIn')}
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
}

interface AccountSectionProps {
  session: ReturnType<typeof useSession>['data'];
  status: ReturnType<typeof useSession>['status'];
  onSignOut: () => void;
}

function AccountSection({ session, status, onSignOut }: AccountSectionProps) {
  const t = useTranslations('Header');

  if (status === 'loading') return null;

  if (status === 'authenticated' && session?.user) {
    const initial = (session.user.name?.[0] || session.user.email?.[0] || 'U').toUpperCase();
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 px-2 py-1 rounded-full border border-[#233554] hover:border-[#64ffda] transition-colors focus:outline-none">
            <div className="w-7 h-7 rounded-full bg-[#64ffda] text-[#0a192f] flex items-center justify-center font-bold text-sm flex-shrink-0">
              {initial}
            </div>
            <span className="text-sm text-white hidden xl:block max-w-[120px] truncate">
              {session.user.name || session.user.email}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#112240] border-[#233554] text-white min-w-[200px]">
          <DropdownMenuLabel className="pb-2">
            <div className="font-semibold text-white truncate">
              {session.user.name || t('user')}
            </div>
            <div className="text-xs text-[#a8b2d1] truncate">{session.user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[#233554]" />
          <DropdownMenuItem
            onClick={onSignOut}
            className="text-red-400 hover:text-red-300 focus:text-red-300 hover:bg-[#0a192f] focus:bg-[#0a192f] cursor-pointer gap-2"
          >
            <LogOut className="w-4 h-4" />
            {t('signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Link
      href="/auth/signin"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#233554] hover:border-[#64ffda] text-[#a8b2d1] hover:text-[#64ffda] transition-colors text-sm"
    >
      <LogIn className="w-4 h-4" />
      <span className="hidden xl:block">{t('signIn')}</span>
    </Link>
  );
}
